import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { IinventoryItem, IstoreItem } from '@typings/store/storeItem.interface';
import { PET_SUB_FUNCTION_TYPE } from '@typings/store/storeItemFunctionType';
import IconButton from '@components/dataEntry/button/IconButton';
import { useCollection } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import ItemImgCard from '@components/dataDisplay/Store/ItemImgCard';

interface Props {
  type: 'food' | 'toy';
  items: IinventoryItem[]; // 실제 인벤토리 아이템 사용
  onUseItem: (
    itemId: string,
    quantity: number,
    label?: string,
  ) => Promise<void>;
  disabled?: boolean;
  petHunger?: number; // 배고픔 상태 (food 타입에서 사용)
  petMaxHunger?: number; // 최대 배고픔 상태 (food 타입에서 사용)
}

const ItemDropdown: React.FC<Props> = ({
  type,
  items,
  onUseItem,
  disabled = false,
  petHunger = 0,
  petMaxHunger = 100,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<IinventoryItem | null>(null);
  const [useQuantity, setUseQuantity] = useState(1);
  const [pending, setPending] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 스토어 아이템 전체 로드 → 인벤토리 아이템과 병합
  const { data: storeItems } = useCollection<IstoreItem>(CollectionPath.STORE, {
    listen: true,
  });

  const storeMap = useMemo(() => {
    const map = new Map<string, IstoreItem>();
    (storeItems || []).forEach((s) => {
      if (s.id) map.set(s.id, s);
    });
    return map;
  }, [storeItems]);

  const mergedItems: (IinventoryItem & Partial<IstoreItem>)[] = useMemo(() => {
    return (items || []).map((inv) => {
      const origin = inv.originItemID
        ? storeMap.get(inv.originItemID)
        : undefined;
      return {
        ...inv,
        // 원본(스토어) 필드로 보강
        title: (inv as any).title || (origin as any)?.title,
        description: (inv as any).description || (origin as any)?.description,
        imgURL:
          (inv as any).imgURL ||
          (inv as any).customImgURL ||
          (origin as any)?.imgURL,
        functionType:
          (inv as any).functionType || (origin as any)?.functionType,
        petFunction: (inv as any).petFunction || (origin as any)?.petFunction,
      } as any;
    });
  }, [items, storeMap]);

  // 타입별 필터링 (PET_SUB_FUNCTION_TYPE 기반) - 병합된 데이터 기준
  const filteredItems = mergedItems.filter((item) => {
    const subType = (item as any)?.petFunction?.subFunctionType;
    if (!subType) return false;
    if (type === 'food') return subType === PET_SUB_FUNCTION_TYPE.FILL_HUNGER;
    if (type === 'toy') return subType === PET_SUB_FUNCTION_TYPE.FILL_EXP;
    return false;
  });

  // 사용 가능한 아이템 (multiple 배열 길이 > 0)
  const availableItems = filteredItems.filter(
    (item) => item.multiple && item.multiple.length > 0,
  );

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 🎯 아이템 사용
  const handleUseItem = async () => {
    if (!selectedItem || pending) return;

    // 배고픔이 최대일 때 음식 사용 금지
    if (type === 'food' && petHunger >= petMaxHunger) {
      alert('펫이 배가 부릅니다! 더 이상 음식을 줄 수 없어요.');
      return;
    }

    setPending(true);
    try {
      const label = getItemLabel(selectedItem);
      await onUseItem(selectedItem.id, useQuantity, label);
      setIsOpen(false);
      setSelectedItem(null);
      setUseQuantity(1);
    } catch (error) {
      console.error('아이템 사용 실패:', error);
      alert('아이템 사용에 실패했습니다.');
    } finally {
      setPending(false);
    }
  };

  // 라벨 텍스트
  // 버튼 라벨: 사용 가능한 총 개수(중복 포함) 표시
  const getButtonLabel = () => {
    const totalUsableCount = availableItems.reduce((sum, it) => {
      return sum + (it.multiple ? it.multiple.length : 0);
    }, 0);
    if (type === 'food') {
      return `밥주기 (${totalUsableCount})`;
    } else {
      return `놀아주기 (${totalUsableCount})`;
    }
  };

  // 아이템 효과 텍스트
  const getEffectText = (
    item: IinventoryItem | (IinventoryItem & Partial<IstoreItem>),
  ) => {
    const anyItem: any = item as any;
    const value = anyItem?.petFunction?.value || 0;
    const effectType = anyItem?.petFunction?.subFunctionType;

    if (effectType === PET_SUB_FUNCTION_TYPE.FILL_HUNGER) {
      return `배고픔 +${value}`;
    } else if (effectType === PET_SUB_FUNCTION_TYPE.FILL_EXP) {
      return `경험치 +${value}`;
    }
    return `+${value}`;
  };

  // 아이템 라벨 안전 처리
  const getItemLabel = (
    item?: (IinventoryItem & Partial<IstoreItem>) | null,
  ) => {
    if (!item) return '아이템';
    const anyItem = item as any;
    return (
      item.title ||
      anyItem.customTitle ||
      anyItem.name ||
      item.originItemID ||
      '아이템'
    );
  };

  // 아이템 개수 가져오기
  const getItemQuantity = (item: IinventoryItem) => {
    return item.multiple ? item.multiple.length : 0;
  };

  // 선택된 아이템의 실제 보유 개수(중복 포함)를 안전하게 가져오기
  const getItemQuantityById = (itemId?: string) => {
    if (!itemId) return 0;
    const src = items.find((i) => i.id === itemId);
    return src && src.multiple ? src.multiple.length : 0;
  };

  // 비활성화 조건
  const isDisabled =
    disabled ||
    availableItems.length === 0 ||
    (type === 'food' && petHunger >= petMaxHunger);

  return (
    <Container ref={dropdownRef}>
      <DropdownButton
        onClick={() => setIsOpen(!isOpen)}
        $type={type}
        $isOpen={isOpen}
      >
        <ButtonText>{getButtonLabel()}</ButtonText>
        <Arrow $isOpen={isOpen}>▼</Arrow>
      </DropdownButton>

      {isOpen && (
        <DropdownMenu>
          <ItemList>
            {availableItems.length === 0 ? (
              <EmptyMessage>
                {type === 'food'
                  ? '사용할 수 있는 음식이 없어요'
                  : '사용할 수 있는 놀이 아이템이 없어요'}
              </EmptyMessage>
            ) : (
              availableItems.map((item) => (
                <DropdownRow
                  key={item.id}
                  item={item as any}
                  type={type}
                  isSelected={selectedItem?.id === item.id}
                  onSelect={(merged) => setSelectedItem(merged as any)}
                  getEffectText={getEffectText}
                  getQuantity={getItemQuantity}
                />
              ))
            )}
          </ItemList>

          {selectedItem && type === 'food' && petHunger >= petMaxHunger && (
            <WarningText>⚠️ 배가 가득해요!</WarningText>
          )}

          {selectedItem && !isDisabled && (
            <UseSection>
              <QuantitySelector>
                <QuantityLabel>사용 개수:</QuantityLabel>
                <QuantityControls>
                  <IconButton
                    onClick={() => setUseQuantity(Math.max(1, useQuantity - 1))}
                    disabled={useQuantity <= 1}
                    size="sm"
                    shape="stroke"
                    style={{ fontSize: '20px' }}
                  >
                    -
                  </IconButton>
                  <QuantityDisplay>{useQuantity}</QuantityDisplay>
                  <IconButton
                    onClick={() =>
                      setUseQuantity(
                        Math.min(
                          getItemQuantityById(selectedItem?.id),
                          useQuantity + 1,
                        ),
                      )
                    }
                    disabled={
                      useQuantity >= getItemQuantityById(selectedItem?.id)
                    }
                    size="sm"
                    shape="stroke"
                    style={{ fontSize: '20px' }}
                  >
                    +
                  </IconButton>
                </QuantityControls>
              </QuantitySelector>

              <UseButton
                onClick={handleUseItem}
                disabled={pending}
                $type={type}
              >
                {pending ? (
                  <>
                    <LoadingSpinner />
                    사용 중...
                  </>
                ) : (
                  `${getItemLabel(selectedItem)} ${useQuantity}개 사용`
                )}
              </UseButton>
            </UseSection>
          )}
        </DropdownMenu>
      )}
    </Container>
  );
};

// 단일 행: 원본 아이템과 병합하여 표시
const DropdownRow: React.FC<{
  item: IinventoryItem;
  type: 'food' | 'toy';
  isSelected: boolean;
  onSelect: (merged: IinventoryItem & Partial<IstoreItem>) => void;
  getEffectText: (item: IinventoryItem) => string;
  getQuantity: (item: IinventoryItem) => number;
}> = ({ item, type, isSelected, onSelect, getEffectText, getQuantity }) => {
  const originId = item.originItemID;
  // 상단에서 store 전체를 병합해 왔으므로 여기서는 개별 useDocument 호출을 제거
  const origin = null as any;

  const merged: IinventoryItem & Partial<IstoreItem> = {
    ...item,
  } as any;

  const quantity = getQuantity(item);

  return (
    <ItemRow
      onClick={() => onSelect(merged)}
      $isSelected={isSelected}
      $rarity="common"
      $type={type}
    >
      <ItemIcon>
        <ItemImgCard data={merged} style={{ width: 28, height: 28 }} />
      </ItemIcon>
      <ItemInfo>
        <ItemName>
          {(merged as any).title || originId || '아이템'} ({quantity}개)
        </ItemName>
        <ItemEffect>{getEffectText(item)}</ItemEffect>
        {merged.description && (
          <ItemDescription>{merged.description}</ItemDescription>
        )}
      </ItemInfo>
    </ItemRow>
  );
};

// 🎨 스타일 컴포넌트
const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button<{
  $type: string;
  $isOpen: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 120px;
  padding: 8px 12px;
  background: ${({ $type }) => ($type === 'food' ? '#4CAF50' : '#2196F3')};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ $isOpen }) =>
    $isOpen &&
    `
    border-radius: 8px 8px 0 0;
  `}
`;

const ButtonText = styled.span`
  flex: 1;
  text-align: left;
`;

const Arrow = styled.span<{ $isOpen: boolean }>`
  margin-left: 8px;
  font-size: 10px;
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--lineColor);
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  z-index: 3;
  max-height: 300px;
  overflow-y: auto;
`;

const WarningText = styled.div`
  font-size: 11px;
  color: #f44336;
  padding: 12px;
`;

const ItemList = styled.div`
  max-height: 150px;
  overflow-y: auto;
`;

const EmptyMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 12px;
`;

const ItemRow = styled.div<{
  $isSelected: boolean;
  $rarity: string;
  $type: string;
}>`
  display: flex;
  align-items: center;
  padding: 10px 6px;
  cursor: pointer;
  transition: background 0.2s ease;
  background: var(--backgroundColorSolid);
  color: var(--textColor);

  &:hover {
    background: var(--inkColor);
  }

  ${({ $isSelected, $type }) =>
    $isSelected &&
    ($type === 'food'
      ? `background:var(--inkColor); border-left-color: #4CAF50;`
      : `background:var(--inkColor); border-left-color: #2196F3;`)}
`;

const ItemIcon = styled.div`
  font-size: 18px;
  margin-right: 10px;
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 2px;
`;

const ItemEffect = styled.div`
  font-size: 11px;
  color: var(--subTextColor);
`;

const ItemDescription = styled.div`
  font-size: 10px;
  color: var(--subTextColor);
  margin-top: 4px;
`;

const UseSection = styled.div`
  padding: 12px;
  background: var(--backgroundColorSolid);
  color: var(--textColor);
  border-top: 1px solid var(--lineColor);
`;

const QuantitySelector = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const QuantityLabel = styled.div`
  font-size: 12px;
  color: var(--subTextColor);
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QuantityDisplay = styled.div`
  min-width: 30px;
  text-align: center;
  font-size: 12px;
  font-weight: bold;
`;

const UseButton = styled.button<{ $type: string }>`
  width: 100%;
  padding: 8px;
  background: ${({ $type }) => ($type === 'food' ? '#4CAF50' : '#2196F3')};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default ItemDropdown;
